import { useState } from 'react';
import type { CwDatasetState, CwSyncOutcome } from '@catalyst-psa/shared';
import { CW_DATASET_LABELS } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { formatDateTime } from '../../utils/date.js';
import { useSyncCwDataset, useUpdateCwDataset } from './api.js';
import { DatasetTestModal } from './DatasetTestModal.js';
import { FieldMappingModal } from './FieldMappingModal.js';

const OUTCOME_TONES: Record<CwSyncOutcome, 'green' | 'amber' | 'red' | 'blue'> = {
  ok: 'green',
  partial: 'amber',
  failed: 'red',
  running: 'blue',
};

interface DatasetCardProps {
  dataset: CwDatasetState;
  isAdmin: boolean;
  configured: boolean;
}

export function DatasetCard({ dataset, isAdmin, configured }: DatasetCardProps): JSX.Element {
  const updateDataset = useUpdateCwDataset();
  const syncDataset = useSyncCwDataset();

  const [scheduled, setScheduled] = useState(dataset.enabled);
  const [interval, setInterval] = useState(
    dataset.intervalMinutes ? String(dataset.intervalMinutes) : '60',
  );
  const [applyLastInvoice, setApplyLastInvoice] = useState(
    dataset.applyLastInvoiceToBilledThrough,
  );
  const [resyncLines, setResyncLines] = useState(dataset.resyncAgreementLines);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const { implemented } = dataset;
  const canControl = isAdmin && implemented;
  const isRunning = dataset.lastOutcome === 'running';

  const handleSaveSchedule = (): void => {
    const minutes = Number(interval);
    updateDataset.mutate({
      dataset: dataset.dataset,
      input: {
        enabled: scheduled,
        intervalMinutes: scheduled && minutes > 0 ? Math.floor(minutes) : null,
        ...(dataset.dataset === 'agreements'
          ? {
              applyLastInvoiceToBilledThrough: applyLastInvoice,
              resyncAgreementLines: resyncLines,
            }
          : {}),
      },
    });
  };

  const handleRun = (): void => {
    syncDataset.mutate({ dataset: dataset.dataset, input: {} });
  };

  const handleFullResync = (): void => {
    const confirmed = window.confirm(
      `Run a full re-sync of ${CW_DATASET_LABELS[dataset.dataset]}? This re-fetches every record from ConnectWise (ignoring the change watermark) and re-applies the current field mappings. Existing records are updated only where a mapped value changed.`,
    );
    if (!confirmed) return;
    syncDataset.mutate({ dataset: dataset.dataset, input: { since: '2000-01-01' } });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            {CW_DATASET_LABELS[dataset.dataset]}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {implemented
              ? 'Syncs into native records tagged with their ConnectWise id.'
              : 'Coming soon.'}
          </p>
        </div>
        {implemented ? (
          dataset.lastOutcome ? (
            <Badge tone={OUTCOME_TONES[dataset.lastOutcome]}>{dataset.lastOutcome}</Badge>
          ) : (
            <Badge tone="gray">Never run</Badge>
          )
        ) : (
          <Badge tone="gray">Coming soon</Badge>
        )}
      </div>

      {implemented ? (
        <>
          <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Last sync</dt>
              <dd className="text-slate-800">{formatDateTime(dataset.lastSyncAt)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Next run</dt>
              <dd className="text-slate-800">{formatDateTime(dataset.nextRunAt)}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-500">Last result</dt>
              <dd className="text-slate-800">
                {isRunning
                  ? 'Syncing…'
                  : dataset.lastOutcome
                    ? `created ${dataset.created}, updated ${dataset.updated}, unchanged ${dataset.unchanged}, failed ${dataset.failed}`
                    : '—'}
              </dd>
            </div>
          </dl>

          <div className="mb-4 rounded-lg bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scheduled}
                disabled={!canControl}
                onChange={(e) => setScheduled(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="font-medium text-slate-700">Scheduled sync</span>
            </label>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm text-slate-600">Every</span>
              <input
                type="number"
                min={1}
                value={interval}
                disabled={!canControl || !scheduled}
                onChange={(e) => setInterval(e.target.value)}
                className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
              />
              <span className="text-sm text-slate-600">minutes</span>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleSaveSchedule}
                isLoading={updateDataset.isPending}
                disabled={!canControl}
                className="ml-auto"
              >
                Save schedule
              </Button>
            </div>
            {dataset.dataset === 'agreements' ? (
              <>
                <label className="mt-3 flex items-start gap-2 border-t border-slate-200 pt-3 text-sm">
                  <input
                    type="checkbox"
                    checked={resyncLines}
                    disabled={!canControl}
                    onChange={(e) => setResyncLines(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <span className="font-medium text-slate-700">
                      Re-scan agreement lines every sync
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      ConnectWise does not flag an agreement as changed when you add or edit
                      one of its additions, so new lines never reach Catalyst on a normal
                      sync. With this on, every synced agreement that is not ended has its
                      lines re-checked (one extra ConnectWise request per agreement, so syncs
                      take longer).
                    </span>
                  </span>
                </label>
                <label className="mt-3 flex items-start gap-2 border-t border-slate-200 pt-3 text-sm">
                  <input
                    type="checkbox"
                    checked={applyLastInvoice}
                    disabled={!canControl}
                    onChange={(e) => setApplyLastInvoice(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <span className="font-medium text-slate-700">
                      Set billed-through from last CW invoice
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      On each agreement sync, stamps every line&apos;s billed-through date from
                      the agreement&apos;s most recent ConnectWise invoice. Intended as a one-off
                      backfill — turn it off afterward so PSA billing stays the source of truth.
                    </span>
                  </span>
                </label>
              </>
            ) : null}
          </div>

          {syncDataset.isError ? (
            <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
              Sync failed. Please try again.
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleRun}
              isLoading={syncDataset.isPending || isRunning}
              disabled={!canControl || !configured || isRunning}
            >
              Run now
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleFullResync}
              isLoading={syncDataset.isPending || isRunning}
              disabled={!canControl || !configured || isRunning}
            >
              Full re-sync
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setTestOpen(true)}
              disabled={!canControl || !configured}
            >
              Test
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setMappingOpen(true)}
              disabled={!isAdmin}
            >
              Configure mapping
            </Button>
          </div>

          {!configured ? (
            <p className="mt-2 text-xs text-slate-500">
              Configure ConnectWise credentials on the server to enable syncing.
            </p>
          ) : null}

          <FieldMappingModal
            dataset={dataset.dataset}
            isOpen={mappingOpen}
            onClose={() => setMappingOpen(false)}
          />

          <DatasetTestModal
            dataset={dataset.dataset}
            isOpen={testOpen}
            onClose={() => setTestOpen(false)}
          />
        </>
      ) : (
        <p className="text-sm text-slate-500">
          This dataset will be available in an upcoming release.
        </p>
      )}
    </div>
  );
}
